// Issue #188 — the leading checkbox on a template's packing rows. A template's box only
// ever *stages*: there is nothing to pack until the template has been imported into a
// trip, and what it records is carried onto the trip row the import creates.
//
// It differs from the trip's `CompletionControl` in whose state it shows. A trip's box
// is "did *I* check this off?" — a template has no "me" (the editor need not be one of
// its travelers), so this box stands for the row: ticked when every traveler it is for
// is staged, half-ticked when some are, and a click moves the whole row. Individual
// travelers are still staged one at a time through the row's assignment chips.

import type { ReactNode } from "react";
import { Checkbox } from "@pack-it-up/ui-web";
import { checkedCount, requiredCount, type Completable } from "@pack-it-up/core";
import type { ParticipantId } from "@pack-it-up/shared";

export const StagingCheckbox = ({
  item,
  participants,
  canEdit,
  onToggle,
}: {
  item: Completable & { name: string };
  // The travelers the row is for — `relevantParticipants` from the caller.
  participants: ParticipantId[];
  canEdit: boolean;
  onToggle: () => void;
}): ReactNode => {
  const required = requiredCount(item, participants);
  const staged = checkedCount(item, "staging");
  const all = required > 0 && staged === required;
  const some = staged > 0 && !all;
  return (
    <Checkbox
      checked={false}
      {...(all ? { state: "staged" as const } : {})}
      indeterminate={some}
      disabled={!canEdit}
      aria-label={`Stage ${item.name}`}
      onChange={onToggle}
    />
  );
};
